import { Navigate, Route, Routes, Outlet } from "react-router-dom";
import {
  ComponentType,
  LazyExoticComponent,
  ReactNode,
  Suspense,
  lazy,
  useEffect,
} from "react";
import ProtectedRoute from "@/core/auth/ProtectedRoute";
import PublicOnlyRoute from "@/core/auth/PublicOnlyRoute";
import SectorGate from "@/core/auth/SectorGate";
import PermissionGate from "@/core/auth/PermissionGate";
import SettingsGate from "@/features/settings/components/SettingsGate";
import CampaignHealthGate from "@/features/campaigns/components/CampaignHealthGate";
import { RootLayout } from "@/layouts/RootLayout";
import { RoutePaths } from "@/core/constants/RoutePaths";
import Env from "@/core/config/env";
import Loading from "@/shared/components/Loading";
import { SectorAccessGuard } from "@/features/billing/components/SectorAccessGuard";
// Skeletons de página (import estático: o fallback do Suspense precisa estar
// disponível de imediato, senão o próprio fallback suspenderia). São leves e
// espelham o layout de cada página, mantendo a sidebar visível e mostrando o
// conteúdo estático na hora enquanto o chunk/dados carregam.
import { ChatsPageSkeleton } from "@/pages/Chats/skeleton";
import { SettingsPageSkeleton } from "@/features/settings/skeleton";
import { AdvancedTabSkeleton } from "@/features/settings/components/AdvancedTab/skeleton";
import { AttendingTabSkeleton } from "@/features/settings/components/AttendingTab/skeleton";
import { QuickRepliesSettingsTabSkeleton } from "@/features/quick-replies/components/QuickRepliesSettingsTab/skeleton";
import { TagsTabSkeleton } from "@/features/settings/components/TagsTab/skeleton";
import { BillingSettingsTabSkeleton } from "@/features/billing/components/BillingSettingsTab/skeleton";
import { MediaRetentionTabSkeleton } from "@/features/media-retention/components/MediaRetentionTab/skeleton";
import { NotificationSoundTabSkeleton } from "@/features/notification-sound/components/NotificationSoundTab/skeleton";
import { ContactsPageSkeleton } from "@/features/contacts/skeleton";
import { AgentPageSkeleton } from "@/features/agent/skeleton";
import { FollowUpsPageSkeleton } from "@/features/follow-ups/skeleton";
import {
  TeamAreasTabSkeleton,
  TeamManagementPageSkeleton,
  TeamPeopleTabSkeleton,
} from "@/features/team-management/skeleton";
import { AREAS_ENABLED } from "@superagents/types/api";
import { AddWhatsAppPageSkeleton } from "@/features/add-whatsapp/skeleton";
import { DashboardPageSkeleton } from "@/features/dashboard/skeleton";
import { WhatsAppStatusPageSkeleton } from "@/features/whatsapp-status/skeleton";
import {
  IntegrationDetailFallback,
  IntegrationsCatalogFallback,
} from "@/features/integrations/components/IntegrationChrome/skeleton";
// Páginas de autenticação e docs são importadas de forma estática (sem lazy):
// login/signup/forgot/etc. compartilham o AuthLayout e navegar entre elas com
// lazy causava um flash de spinner a cada troca. Docs é 100% estática, então
// também entra inteira de uma vez, sem skeleton.
import LoginPage from "@/features/login/LoginPage";
import SignUpPage from "@/features/signup/SignUpPage";
import { ForgotPasswordPage } from "@/features/forgot-password/ForgotPasswordPage";
import ResetPasswordPage from "@/features/login/ResetPasswordPage";
import EmailConfirmationPage from "@/features/login/EmailConfirmationPage";
import EmailVerifiedPage from "@/features/login/EmailVerifiedPage";
import DocsPage from "@/features/docs/DocsPage";
import {
  AuthenticatedAppProviders,
  ChatsPage,
  MainLayout,
  OpenConversation,
  idlePreloadsFor,
  shouldPrefetchShell,
} from "@/core/boot/shellChunks";
import { readStoredAuthToken } from "@/core/boot/prefetchOnBoot";

const isNonProd = Env.VITE_ENV !== "prod";
const isDev = Env.VITE_ENV === "dev";
// `ComponentType` cobre os `lazyWithPreload` do shell (`core/boot/shellChunks`).
type LazyRouteComponent =
  | LazyExoticComponent<ComponentType<object>>
  | ComponentType<object>;

const AcceptInvitePage = lazy(() =>
  import("./features/accept-invite/AcceptInvitePage").then((module) => ({
    default: module.AcceptInvitePage,
  }))
);
const OnboardingV2Page = lazy(
  () => import("@/features/onboarding-v2/OnboardingV2Page")
);
const AddWhatsAppPage = lazy(
  () => import("@/features/add-whatsapp/AddWhatsAppPage")
);
const GoogleOAuthCallbackPage = lazy(
  () => import("@/features/login/GoogleOAuthCallbackPage")
);
const MetaSignupCallbackPage = lazy(
  () => import("@/features/onboard/MetaSignupCallbackPage")
);
const GoogleCalendarCallbackPage = lazy(
  () => import("@/features/integrations/GoogleCalendarCallbackPage")
);
const BillingGatePage = lazy(() =>
  import("@/features/billing/BillingGatePage").then((module) => ({
    default: module.BillingGatePage,
  }))
);
// Central de Treinamento (SA-1483): só dev, tela cheia, em aba própria.
const AiFeedbackCentralPage = lazy(
  () => import("@/features/ai-feedback-central/AiFeedbackCentralPage")
);
const AiTrainingPage = lazy(
  () => import("@/features/ai-training/AiTrainingPage")
);
const OpenDevAccountPage = lazy(
  () => import("@/features/ai-feedback-central/OpenDevAccountPage")
);
const SettingsPage = lazy(() => import("@/features/settings/SettingsPage"));
const AdvancedTab = lazy(
  () => import("@/features/settings/components/AdvancedTab")
);
const AttendingTab = lazy(
  () => import("@/features/settings/components/AttendingTab")
);
const WhatsAppStatusPage = lazy(
  () => import("@/features/whatsapp-status/WhatsAppStatusPage")
);
const ContactsPage = lazy(() => import("@/features/contacts/ContactsPage"));
const TagsTab = lazy(() => import("@/features/settings/components/TagsTab"));
const NotificationSoundTab = lazy(
  () => import("@/features/notification-sound/components/NotificationSoundTab")
);
const QuickRepliesSettingsTab = lazy(
  () => import("@/features/quick-replies/components/QuickRepliesSettingsTab")
);
const BillingSettingsTab = lazy(() =>
  import("@/features/billing/components/BillingSettingsTab").then((module) => ({
    default: module.BillingSettingsTab,
  }))
);
// SA-1433: Configurações > Retenção de dados (retenção de mídia, admin da empresa).
const MediaRetentionTab = lazy(
  () => import("@/features/media-retention/components/MediaRetentionTab")
);
const DashboardPage = lazy(() => import("@/features/dashboard/DashboardPage"));
const TeamManagementPage = lazy(() =>
  import("./features/team-management/TeamManagementPage").then((module) => ({
    default: module.TeamManagementPage,
  }))
);
// Abas da Equipe (rotas filhas do layout): mesmo chunk da página.
const TeamPeopleTab = lazy(() =>
  import("./features/team-management/TeamManagementPage").then((module) => ({
    default: module.TeamPeopleTab,
  }))
);
const TeamAreasTab = lazy(() =>
  import("./features/team-management/TeamManagementPage").then((module) => ({
    default: module.TeamAreasTab,
  }))
);
const AgentPage = lazy(() =>
  import("@/features/agent/AgentPage").then((module) => ({
    default: module.AgentPage,
  }))
);
const FollowUpsPage = lazy(() => import("@/features/follow-ups/FollowUpsPage"));
const IntegrationsPage = lazy(() =>
  import("@/features/integrations/IntegrationsPage").then((module) => ({
    default: module.IntegrationsPage,
  }))
);
const IntegrationDetailPage = lazy(() =>
  import("@/features/integrations/IntegrationDetailPage").then((module) => ({
    default: module.IntegrationDetailPage,
  }))
);
const CampaignBuilderPage = lazy(
  () => import("@/features/campaigns/CampaignBuilderPage")
);
const CampaignReviewPage = lazy(
  () => import("@/features/campaigns/CampaignReviewPage")
);
const CampaignsListPage = lazy(
  () => import("@/features/campaigns/CampaignsListPage")
);
const CampaignDetailPage = lazy(
  () => import("@/features/campaigns/CampaignDetailPage")
);
const AutomationsListPage = lazy(
  () => import("@/features/automations/AutomationsListPage")
);
const AutomationBuilderPage = lazy(
  () => import("@/features/automations/AutomationBuilderPage")
);
const AutomationDetailPage = lazy(
  () => import("@/features/automations/AutomationDetailPage")
);
// Documentos legais — públicos, mas em lazy: quem está usando o app logado
// nunca baixa o chunk do markdown sem abrir a página.
const LegalDocumentPage = lazy(() =>
  import("@/features/legal/LegalDocumentPage").then((module) => ({
    default: module.LegalDocumentPage,
  }))
);

// Calculadora pública de plano — mesmo motivo do lazy acima: quem usa o app
// logado nunca baixa esse chunk.
const PlanCalculatorPage = lazy(() =>
  import("@/features/plan-calculator/PlanCalculatorPage").then((module) => ({
    default: module.PlanCalculatorPage,
  }))
);

const CatalogPage = isNonProd
  ? lazy(() => import("@/design-system/catalog/CatalogPage"))
  : null;

const PreviewPage = isNonProd
  ? lazy(() => import("@/preview/PreviewPage"))
  : null;

function renderLazyPage(
  Component: LazyRouteComponent,
  fallback: ReactNode = <Loading />
) {
  return <Suspense fallback={fallback}>{<Component />}</Suspense>;
}

const MainLayoutWrapper = () => (
  <MainLayout>
    <Suspense fallback={<Loading />}>
      <Outlet />
    </Suspense>
  </MainLayout>
);

export default function AppRoutes() {
  // Pré-carrega os chunks do shell autenticado durante o idle. Sem isso, a
  // primeira transição (login → onboarding/chats) mostrava o spinner genérico
  // enquanto os chunks do MainLayout/providers/página baixavam.
  useEffect(() => {
    // Vale a página de entrada, lida uma vez: `useLocation` aqui refaria a
    // árvore de rotas inteira a cada navegação.
    if (!shouldPrefetchShell(window.location.pathname)) return;
    const prefetch = () => {
      void AuthenticatedAppProviders.preload();
      void MainLayout.preload();
      void ChatsPage.preload();
      const extra = idlePreloadsFor(
        window.location.pathname,
        Boolean(readStoredAuthToken())
      );
      if (extra.onboarding) {
        import("@/features/onboarding-v2/OnboardingV2Page");
      }
      if (extra.openConversation) void OpenConversation.preload();
    };
    const ric = window.requestIdleCallback;
    if (ric) {
      const id = ric(prefetch);
      return () => window.cancelIdleCallback?.(id);
    }
    const timer = window.setTimeout(prefetch, 1500);
    return () => window.clearTimeout(timer);
  }, []);

  return (
    <Routes>
      <Route element={<RootLayout />}>
        {/* Rotas Públicas (Login, Signup, etc) — importadas de forma estática
            (sem Suspense) para não piscar um spinner ao trocar entre elas. */}
        <Route element={<PublicOnlyRoute />}>
          <Route
            path={RoutePaths.SIGNUP}
            element={<SignUpPage />}
          />
          <Route
            path={RoutePaths.LOGIN}
            element={<LoginPage />}
          />
          <Route
            path={RoutePaths.FORGOT_PASSWORD}
            element={<ForgotPasswordPage />}
          />
          <Route
            path={RoutePaths.ACCEPT_INVITE}
            element={renderLazyPage(AcceptInvitePage)}
          />
          <Route
            path={RoutePaths.RESET_PASSWORD}
            element={<ResetPasswordPage />}
          />
          <Route
            path={RoutePaths.EMAIL_CONFIRMATION}
            element={<EmailConfirmationPage />}
          />
          <Route
            path={RoutePaths.EMAIL_VERIFIED}
            element={<EmailVerifiedPage />}
          />
        </Route>

        {/* Rotas Protegidas (Requer Login) */}
        <Route element={<ProtectedRoute />}>
          <Route element={renderLazyPage(AuthenticatedAppProviders)}>
            <Route
              path={RoutePaths.ONBOARDING}
              element={renderLazyPage(OnboardingV2Page)}
            />

            <Route
              path={RoutePaths.ADD_WHATSAPP}
              element={renderLazyPage(
                AddWhatsAppPage,
                <AddWhatsAppPageSkeleton />
              )}
            />

            {/* Central de Treinamento (SA-1483): não depende do setor ativo
                (feedbacks de todas as contas), por isso fica fora do
                SectorGate. O atalho de abrir conta troca o setor e segue
                para a conversa. */}
            <Route
              path={RoutePaths.AI_FEEDBACK_CENTRAL}
              element={renderLazyPage(AiFeedbackCentralPage)}
            />
            {/* Área de Treinamento (SA-1488): mesmo modelo da central, só dev. */}
            <Route
              path={RoutePaths.AI_TRAINING}
              element={renderLazyPage(AiTrainingPage)}
            />
            <Route
              path={RoutePaths.DEV_OPEN_ACCOUNT}
              element={renderLazyPage(OpenDevAccountPage)}
            />

            {/* Rotas que requerem Setor Definido */}
            <Route element={<SectorGate />}>
              {/* Gate de billing — página própria, FORA do MainLayout (tela
                  cheia, sem header/sidebar). O SectorAccessGuard redireciona
                  para cá quando o billing trava o acesso; também recebe o
                  retorno do Stripe Checkout. */}
              <Route
                path={RoutePaths.PLAN_GATE}
                element={renderLazyPage(BillingGatePage)}
              />
              <Route element={<MainLayoutWrapper />}>
                {/* SectorAccessGuard envolve apenas o Outlet interno —
                    header/sidebar/Account Switcher do MainLayout continuam
                    visíveis para o usuário trocar de Company mesmo bloqueado. */}
                <Route
                  element={
                    <SectorAccessGuard>
                      <Outlet />
                    </SectorAccessGuard>
                  }>
                  {/* Páginas sem a chave aparecem bloqueadas, nunca somem
                      (SA-1352 D10): o PermissionGate de cada uma renderiza o
                      bloqueio de recurso no lugar dela. Conversas nunca é
                      gateada. */}
                  {/* Configurações: PermissionGate de ui:settings:view, com a
                      exceção de Faturamento para o admin da empresa (é da
                      empresa, não do setor). */}
                  <Route element={<SettingsGate />}>
                    <Route
                      path={RoutePaths.SETTINGS}
                      element={renderLazyPage(
                        SettingsPage,
                        <SettingsPageSkeleton />
                      )}>
                      <Route
                        index
                        element={
                          <Navigate
                            to={RoutePaths.SETTINGS_ATTENDINGS}
                            replace
                          />
                        }
                      />
                      <Route
                        path="advanced"
                        element={renderLazyPage(
                          AdvancedTab,
                          <AdvancedTabSkeleton />
                        )}
                      />
                      <Route
                        path="attendings"
                        element={renderLazyPage(
                          AttendingTab,
                          <AttendingTabSkeleton />
                        )}
                      />
                      <Route
                        path="whatsapp"
                        element={renderLazyPage(
                          WhatsAppStatusPage,
                          <WhatsAppStatusPageSkeleton />
                        )}
                      />
                      <Route
                        path="tags"
                        element={renderLazyPage(TagsTab, <TagsTabSkeleton />)}
                      />
                      <Route
                        path="notifications"
                        element={renderLazyPage(
                          NotificationSoundTab,
                          <NotificationSoundTabSkeleton />
                        )}
                      />
                      <Route
                        path="quick-replies"
                        element={renderLazyPage(
                          QuickRepliesSettingsTab,
                          <QuickRepliesSettingsTabSkeleton />
                        )}
                      />
                      <Route
                        path="billing"
                        element={renderLazyPage(
                          BillingSettingsTab,
                          <BillingSettingsTabSkeleton />
                        )}
                      />
                      <Route
                        path="data-retention"
                        element={renderLazyPage(
                          MediaRetentionTab,
                          <MediaRetentionTabSkeleton />
                        )}
                      />
                    </Route>
                  </Route>
                  <Route path={RoutePaths.CHATS}>
                    <Route
                      index
                      element={renderLazyPage(ChatsPage, <ChatsPageSkeleton />)}
                    />
                    <Route
                      path=":documentId"
                      element={renderLazyPage(ChatsPage, <ChatsPageSkeleton />)}
                    />
                  </Route>
                  <Route
                    element={
                      <PermissionGate requiredPermission="ui:contacts:view" />
                    }>
                    <Route
                      path={RoutePaths.CONTACTS}
                      element={renderLazyPage(
                        ContactsPage,
                        <ContactsPageSkeleton />
                      )}
                    />
                  </Route>
                  <Route
                    element={
                      <PermissionGate requiredPermission="ui:dashboard:view" />
                    }>
                    <Route
                      path={RoutePaths.DASHBOARD}
                      element={renderLazyPage(
                        DashboardPage,
                        <DashboardPageSkeleton />
                      )}
                    />
                  </Route>
                  <Route
                    element={
                      <PermissionGate requiredPermission="ui:agent:view" />
                    }>
                    <Route
                      path={RoutePaths.AGENT}
                      element={renderLazyPage(AgentPage, <AgentPageSkeleton />)}
                    />
                  </Route>
                  {/* Fora do plano (hasFollowUps, o `planFeature` da página em
                      LOCKABLE_PAGE_COPY) a rota mostra o bloqueio de plano,
                      antes da permissão (SA-1401). */}
                  <Route
                    element={
                      <PermissionGate requiredPermission="ui:follow-ups:view" />
                    }>
                    <Route
                      path={RoutePaths.FOLLOW_UPS}
                      element={renderLazyPage(
                        FollowUpsPage,
                        <FollowUpsPageSkeleton />
                      )}
                    />
                  </Route>
                  <Route
                    element={
                      <PermissionGate requiredPermission="ui:team-management:view" />
                    }>
                    {/* Layout da Equipe (abas + Outlet). Áreas é aba com
                        gate próprio, dentro do de Equipe (SA-1367, R11): sem
                        a chave, o bloqueio fica abaixo das abas. */}
                    <Route
                      path={RoutePaths.TEAM_MANAGEMENT}
                      element={renderLazyPage(
                        TeamManagementPage,
                        <TeamManagementPageSkeleton />
                      )}>
                      <Route
                        index
                        element={renderLazyPage(
                          TeamPeopleTab,
                          <TeamPeopleTabSkeleton />
                        )}
                      />
                      {AREAS_ENABLED && (
                        <Route
                          element={
                            <PermissionGate requiredPermission="ui:areas:view" />
                          }>
                          <Route
                            path={RoutePaths.TEAM_MANAGEMENT_AREAS}
                            element={renderLazyPage(
                              TeamAreasTab,
                              <TeamAreasTabSkeleton />
                            )}
                          />
                        </Route>
                      )}
                    </Route>
                  </Route>
                  <Route
                    element={
                      <PermissionGate requiredPermission="ui:integrations:view" />
                    }>
                    <Route
                      path={RoutePaths.INTEGRATIONS}
                      element={renderLazyPage(
                        IntegrationsPage,
                        <IntegrationsCatalogFallback />
                      )}
                    />
                    <Route
                      path={RoutePaths.INTEGRATIONS_DETAIL}
                      element={renderLazyPage(
                        IntegrationDetailPage,
                        <IntegrationDetailFallback />
                      )}
                    />
                    {/* Instalar/reconfigurar exige o write de API. É gate de
                        ação dentro da página: sem ele, volta para o catálogo. */}
                    <Route
                      element={
                        <PermissionGate
                          requiredPermission={
                            "api:integration-installation:create"
                          }
                          fallback="redirect"
                          redirectTo={RoutePaths.INTEGRATIONS}
                        />
                      }>
                      <Route
                        path={RoutePaths.INTEGRATIONS_INSTALL}
                        element={renderLazyPage(
                          IntegrationDetailPage,
                          <IntegrationDetailFallback />
                        )}
                      />
                    </Route>
                  </Route>
                  {/* Disparos (manuais e automáticos) sob uma chave só. Layout
                      route sem path: não mexe no ranking das rotas irmãs. Fora
                      do plano (hasBulkSend, o `planFeature` da página em
                      LOCKABLE_PAGE_COPY), toda rota daqui mostra o bloqueio de
                      plano, antes da permissão (SA-1401). */}
                  <Route
                    element={
                      <PermissionGate requiredPermission="ui:campaigns:view" />
                    }>
                    {/* Disparo em massa (SA-1170): a feature aparece sempre — a
                        lista/histórico é acessível mesmo sem conexão pronta. */}
                    <Route
                      path={RoutePaths.CAMPAIGNS}
                      element={renderLazyPage(CampaignsListPage)}
                    />
                    {/* Gate de prontidão de disparo (SA-1170/1196): setor
                        não-oficial, desconectado, pendência de pagamento ou envio
                        BLOCKED barram a ENTRADA do builder/revisão — lista e
                        detalhe seguem acessíveis (só a AÇÃO de disparar é travada). */}
                    <Route element={<CampaignHealthGate />}>
                      <Route
                        path={RoutePaths.CAMPAIGN_NEW}
                        element={renderLazyPage(CampaignBuilderPage)}
                      />
                      <Route
                        path={RoutePaths.CAMPAIGN_NEW_REVIEW}
                        element={renderLazyPage(CampaignReviewPage)}
                      />
                      <Route
                        path={RoutePaths.CAMPAIGN_EDIT}
                        element={renderLazyPage(CampaignBuilderPage)}
                      />
                      <Route
                        path={RoutePaths.CAMPAIGN_EDIT_REVIEW}
                        element={renderLazyPage(CampaignReviewPage)}
                      />
                    </Route>
                    {/* Disparos automáticos (SA-1319): rotas irmãs sob
                        /campaigns/automations. O segmento é estático, então o
                        ranking do React Router as coloca acima de
                        /campaigns/:documentId sem depender da ordem aqui. */}
                    <Route
                      path={RoutePaths.AUTOMATIONS}
                      element={renderLazyPage(AutomationsListPage)}
                    />
                    <Route element={<CampaignHealthGate />}>
                      <Route
                        path={RoutePaths.AUTOMATION_NEW}
                        element={renderLazyPage(AutomationBuilderPage)}
                      />
                      <Route
                        path={RoutePaths.AUTOMATION_EDIT}
                        element={renderLazyPage(AutomationBuilderPage)}
                      />
                    </Route>
                    <Route
                      path={RoutePaths.AUTOMATION_DETAIL}
                      element={renderLazyPage(AutomationDetailPage)}
                    />
                    {/* Segmento dinâmico :documentId — o React Router v6 ranqueia
                        estático acima de dinâmico, então /campaigns/new continua
                        vencendo esta rota. */}
                    <Route
                      path={RoutePaths.CAMPAIGN_DETAIL}
                      element={renderLazyPage(CampaignDetailPage)}
                    />
                  </Route>
                </Route>
              </Route>
            </Route>
          </Route>
        </Route>

        {/* Callback OAuth */}
        <Route
          path={RoutePaths.GOOGLE_CALLBACK}
          element={renderLazyPage(GoogleOAuthCallbackPage)}
        />

        {/* Callback do Embedded Signup por redirect (SA-1339). Protegida: o
            callback precisa do usuário logado e do setor ativo para fechar o
            onboarding. */}
        <Route element={<ProtectedRoute />}>
          <Route element={renderLazyPage(AuthenticatedAppProviders)}>
            <Route
              path={RoutePaths.META_SIGNUP_CALLBACK}
              element={renderLazyPage(MetaSignupCallbackPage)}
            />
            {/* Callback do OAuth do Google Calendar (SA-1359). Fora da árvore
                de /integrations de propósito: lá o install exige a permissão
                de write, e um bounce aqui perderia o `code`. */}
            <Route
              path={RoutePaths.GOOGLE_CALENDAR_CALLBACK}
              element={renderLazyPage(GoogleCalendarCallbackPage)}
            />
          </Route>
        </Route>

        {/* Docs — conteúdo 100% estático, renderizado inteiro de uma vez. */}
        <Route
          path={RoutePaths.DOCS}
          element={<DocsPage />}
        />

        {/* Documentos legais — PÚBLICOS. Ficam fora de `ProtectedRoute` e de
            `PublicOnlyRoute` de propósito: precisam abrir para quem não tem
            conta (o aceite do onboarding aponta para cá) e também para quem já
            está logado — `PublicOnlyRoute` jogaria o usuário logado para dentro
            do app. */}
        <Route
          path={RoutePaths.PRIVACY_POLICY}
          element={
            <Suspense fallback={<Loading />}>
              <LegalDocumentPage slug="privacy-policy" />
            </Suspense>
          }
        />
        <Route
          path={RoutePaths.TERMS_OF_USE}
          element={
            <Suspense fallback={<Loading />}>
              <LegalDocumentPage slug="terms-of-use" />
            </Suspense>
          }
        />

        {/* Calculadora pública de plano + custo com a Meta — link de vendas,
            sem login, mesmo motivo dos documentos legais acima. */}
        <Route
          path={RoutePaths.PLAN_CALCULATOR}
          element={renderLazyPage(PlanCalculatorPage)}
        />

        {/* Preview público — apenas em DEV (local). Staging exige auth. */}
        {isDev && PreviewPage && (
          <Route
            path={RoutePaths.PREVIEW}
            element={renderLazyPage(PreviewPage)}
          />
        )}

        {/* Rotas Protegidas (Requer Login) — dev + staging only */}
        {isNonProd && (
          <Route element={<ProtectedRoute />}>
            <Route element={renderLazyPage(AuthenticatedAppProviders)}>
              {CatalogPage && (
                <Route
                  path="/design-system"
                  element={renderLazyPage(CatalogPage)}
                />
              )}
              {/* Preview em staging exige auth */}
              {!isDev && PreviewPage && (
                <Route
                  path={RoutePaths.PREVIEW}
                  element={renderLazyPage(PreviewPage)}
                />
              )}
            </Route>
          </Route>
        )}
      </Route>
    </Routes>
  );
}
